import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Collapse,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Stack,
  alpha
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  DirectionsCar as DriverIcon,
  Restaurant as RestaurantIcon,
  LocalShipping as DeliveryIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ShoppingBag as ShoppingBagIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccountBalanceWallet as WalletIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import config from '../config';
import { fetchUserById } from '../usersApi';
import { useBranch } from '../BranchContext';
import { useTranslation } from 'react-i18next';
import { getSupportedCurrencies } from '../productsApi';

const DeliveryOrders = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [confirmingOrderId, setConfirmingOrderId] = useState(null);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const { selectedBranch } = useBranch();

  const DELIVERY_STAGES = [
    'Pending',
    'Order Confirmed',
    'Processing',
    'Out for Delivery',
    'Delivered'
  ];

  // Load currencies
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const data = await getSupportedCurrencies();
        setCurrencies(data.Currencies || []);
      } catch (error) {
        console.error('Error loading currencies:', error);
        setCurrencies([
          { code: 'YER', symbol: 'ر.ي', name: 'ريال يمني قديم', decimals: 0 },
          { code: 'YRN', symbol: 'ر.ي.ج', name: 'ريال يمني جديد', decimals: 0 },
          { code: 'USD', symbol: '$', name: 'دولار أمريكي', decimals: 2 },
          { code: 'SAR', symbol: 'ر.س', name: 'ريال سعودي', decimals: 2 }
        ]);
      }
    };
    loadCurrencies();
  }, []);

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.symbol : code || 'ر.ي';
  };

  const formatPrice = (amount, currencyCode) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '0';
    const symbol = getCurrencySymbol(currencyCode);
    const currency = currencies.find(c => c.code === currencyCode);
    const decimals = currency ? currency.decimals : 0;
    return `${symbol}${numAmount.toFixed(decimals)}`;
  };

  const getStageTranslation = (stage) => {
    const stageMap = {
      'Pending': t('pending') || 'Pending',
      'Order Confirmed': t('orderStages.confirmation.defaultTitle') || 'Order Confirmed',
      'Processing': t('orderStages.processing.defaultTitle') || 'Processing',
      'Out for Delivery': t('orderStages.deliveryPickup.defaultTitleDelivery') || 'Out for Delivery',
      'Delivered': t('delivered') || 'Delivered'
    };
    return stageMap[stage] || stage;
  };

  const getImageUrl = (prod) => {
    let img = prod.image || prod.Image || prod.ProductImage || prod.productImage || 
              prod.ImageUrl || prod.imageUrl || prod.imageJson;
    
    if (!img) return '';
    
    if (typeof img === 'object' && !Array.isArray(img)) {
      return img.large || img.medium || img.thumbnail || '';
    }
    
    if (typeof img === 'string' && img.startsWith('{')) {
      try {
        const parsed = JSON.parse(img);
        if (parsed.thumbnail || parsed.medium || parsed.large) {
          return parsed.large || parsed.medium || parsed.thumbnail || '';
        }
        if (parsed.url || parsed.path) return parsed.url || parsed.path || '';
      } catch (e) { }
    }
    
    if (Array.isArray(img) && img.length > 0) {
      const first = img[0];
      if (typeof first === 'object') {
        return first.large || first.medium || first.thumbnail || first.url || first.path || '';
      }
      return first || '';
    }
    
    if (typeof img === 'string') {
      if (img.startsWith('http://') || img.startsWith('https://')) return img;
      if (img.startsWith('/images/') || img.startsWith('images/')) {
        if (!img.startsWith('/')) img = '/' + img;
        return `${config.SITE_BASE_URL}${img}`;
      }
      if (img.startsWith('/')) return `${config.SITE_BASE_URL}${img}`;
      return `${config.SITE_BASE_URL}/images/${img}`;
    }
    
    return '';
  };

  // ─── Fetch transaction status for wallet orders ────────
  const fetchTransactionStatus = async (transactionId) => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/Transactions/${transactionId}`);
      const transaction = response.data;
      const statusField = (transaction.Status || '').toLowerCase();
      const approvalApproved = transaction.Approval?.Approved;
      const approved = transaction.Approved;

      if (statusField === 'approved') return 'Approved';
      if (statusField === 'rejected') return 'Rejected';
      if (approvalApproved === true || approvalApproved === 1) return 'Approved';
      if (approved === 1 || approved === '1' || approved === true) return 'Approved';
      if (transaction.Approval?.RejectionReason || transaction.RejectionReason) return 'Rejected';
      return 'Pending';
    } catch {
      return 'Pending';
    }
  };

  // ─── Confirm Order (Pending → Order Confirmed) ────────
  const handleConfirmOrder = async (order) => {
    const orderId = order.orderId || order.OrderId;
    const customerId = order.CustomerId || order.customerId;

    if (!window.confirm(t('admin.confirmOrderMessage') || 'Are you sure you want to confirm this order?')) {
      return;
    }

    try {
      setConfirmingOrderId(orderId);
      await axios.put(`${config.API_BASE_URL}/Orders/UpdateStatus`, {
        OrderId: orderId,
        NewStatus: 'Order Confirmed',
      });

      if (customerId) {
        try {
          await axios.post(`${config.API_BASE_URL}/Notifications/clone-by-type-for-user`, {
            type: 'order-confirmed',
            userId: customerId,
            orderId: orderId,
          });
        } catch (e) { }
      }

      loadOrders();
    } catch (error) {
      console.error('Error confirming order:', error);
      alert(t('admin.confirmOrderError') || 'Failed to confirm order. Please try again.');
    } finally {
      setConfirmingOrderId(null);
    }
  };

  // ─── Cancel/Delete Order ───────────────────────────────
  const handleCancelOrder = async (order) => {
    const orderId = order.orderId || order.OrderId;
    const customerId = order.CustomerId || order.customerId;

    if (!window.confirm(t('admin.cancelOrderMessage') || 'Are you sure you want to cancel and delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      setCancellingOrderId(orderId);
      await axios.delete(`${config.API_BASE_URL}/Orders/${orderId}`);

      if (customerId) {
        try {
          await axios.post(`${config.API_BASE_URL}/Notifications/clone-by-type-for-user`, {
            type: 'order-cancelled',
            userId: customerId,
            orderId: orderId,
          });
        } catch (e) { }
      }

      loadOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert(t('admin.cancelOrderError') || 'Failed to cancel order. Please try again.');
    } finally {
      setCancellingOrderId(null);
    }
  };

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [selectedBranch]);

  const loadOrders = async () => {
    setRefreshing(true);
    try {
      const branchId = selectedBranch ? (selectedBranch.id || selectedBranch.Id) : null;
      
      if (!branchId) {
        setOrders([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const branchUrl = `${config.API_BASE_URL}/Orders?branchId=${branchId}&t=${new Date().getTime()}`;
      const detailsUrl = `${config.API_BASE_URL}/Orders/with-customer-id?t=${new Date().getTime()}`;

      const [branchResponse, detailsResponse] = await Promise.all([
        axios.get(branchUrl),
        axios.get(detailsUrl)
      ]);

      const branchData = branchResponse.data;
      const detailsData = detailsResponse.data;

      const detailsMap = new Map();
      if (Array.isArray(detailsData)) {
        detailsData.forEach((item) => {
          if (item.OrderId || item.orderId) {
            detailsMap.set(item.OrderId || item.orderId, item);
          }
        });
      }

      const mergedData = Array.isArray(branchData) ? branchData.map((order) => {
        const details = detailsMap.get(order.OrderId || order.orderId);
        return {
          ...order,
          DriverId: details?.DriverId || details?.driverId || order.DriverId,
          driverId: details?.DriverId || details?.driverId || order.driverId,
          IsComplete: details?.IsComplete ?? order.IsComplete,
          CustomerId: details?.CustomerId || details?.customerId || order.CustomerId || order.customerId,
          BranchId: details?.BranchId || details?.branchId || order.BranchId || order.branchId,
          DeliveryFee: details?.DeliveryFee !== undefined ? details.DeliveryFee : (order.DeliveryFee !== undefined ? order.DeliveryFee : 0),
          Distance: details?.Distance !== undefined ? details.Distance : (order.Distance !== undefined ? order.Distance : 0),
          Currency: details?.Currency || order.Currency || 'YER',
          currency: details?.Currency || order.Currency || order.currency || 'YER',
          PaymentMethod: details?.PaymentMethod || details?.paymentMethod || order.PaymentMethod || order.paymentMethod,
          TransactionId: details?.TransactionId || details?.transactionId || order.TransactionId || order.transactionId,
          TransactionStatus: details?.TransactionStatus || details?.transactionStatus || order.TransactionStatus || order.transactionStatus,
          // ✅ COUPON: Merge coupon data
          SubTotal: details?.SubTotal !== undefined ? details.SubTotal : (order.SubTotal !== undefined ? order.SubTotal : 0),
          CouponCode: details?.CouponCode || order.CouponCode || null,
          CouponId: details?.CouponId || order.CouponId || null,
          DiscountAmount: details?.DiscountAmount !== undefined ? details.DiscountAmount : (order.DiscountAmount !== undefined ? order.DiscountAmount : 0),
        };
      }) : [];

      let filtered = mergedData.filter(o => {
        const orderBranchId = o.BranchId || o.branchId;
        if (orderBranchId && String(orderBranchId) !== String(branchId)) return false;
        if (o.IsPreOrderDineIn || o.isPreOrderDineIn) return false;
        return true;
      });

      filtered = filtered.filter(o => {
        const status = (o.OrderStatus || o.orderStatus || '').toLowerCase();
        const isCompleted = (o.IsComplete === true || o.IsComplete === 1) || 
                           status === 'delivered' || 
                           status === 'cancelled' || 
                           status === 'completed';
        return !isCompleted;
      });

      // Fetch transaction statuses for wallet orders
      const withTransactions = await Promise.all(filtered.map(async (order) => {
        const paymentMethod = (order.PaymentMethod || order.paymentMethod || '').toLowerCase();
        if (paymentMethod === 'wallet') {
          const transactionId = order.TransactionId || order.transactionId;
          if (transactionId) {
            const txStatus = await fetchTransactionStatus(transactionId);
            return { ...order, TransactionStatus: txStatus, transactionStatus: txStatus };
          }
        }
        return order;
      }));

      const enrichedOrders = await Promise.all(withTransactions.map(async (order) => {
        let customerData = null;
        let driverData = null;

        const customerId = order.CustomerId || order.customerId;
        if (customerId) {
          try {
            customerData = await fetchUserById(customerId);
          } catch (err) { }
        }

        const driverId = order.DriverId || order.driverId;
        if (driverId) {
          try {
            const driverRes = await axios.get(`${config.API_BASE_URL}/Driver/${driverId}`);
            if (driverRes.data) {
              driverData = driverRes.data;
              if (driverData && (driverData.UserId || driverData.userId)) {
                try {
                  const driverUserData = await fetchUserById(driverData.UserId || driverData.userId);
                  driverData = { ...driverData, ...driverUserData };
                } catch (e) { }
              }
            }
          } catch (err) { }
        }

        let products = [];
        if (order.OrderItemsJson) {
          try {
            const parsed = JSON.parse(order.OrderItemsJson);
            const items = Array.isArray(parsed) ? parsed : [parsed];
            products = items.map((prod) => {
              let subProducts = [];
              if (prod.SubProductsJson) {
                if (typeof prod.SubProductsJson === 'string') {
                  try { subProducts = JSON.parse(prod.SubProductsJson); } catch { subProducts = []; }
                } else if (Array.isArray(prod.SubProductsJson)) {
                  subProducts = prod.SubProductsJson;
                }
              } else if (prod.SubProducts) {
                subProducts = prod.SubProducts;
              }
              
              return {
                ProductCode: prod.ProductCode,
                ProductId: prod.ProductId,
                ProductName: prod.ProductName || prod.Name || prod.productName || prod.name || '',
                ProductImage: getImageUrl(prod),
                Size: prod.Size,
                Quantity: prod.Quantity,
                Price: prod.Price,
                Currency: prod.Currency || order.Currency || order.currency || 'YER',
                SubProducts: subProducts
              };
            });
          } catch (err) { }
        }

        return {
          ...order,
          customer: customerData,
          driver: driverData,
          products: products
        };
      }));

      setOrders(enrichedOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const s = status.toLowerCase();
    if (s.includes('pending')) return 'warning';
    if (s.includes('confirmed')) return 'info';
    if (s.includes('processing') || s.includes('preparing')) return 'info';
    if (s.includes('delivery')) return 'primary';
    if (s.includes('delivered')) return 'success';
    return 'default';
  };

  const getStageIndex = (status) => {
    if (!status) return 0;
    const s = status.toLowerCase();
    if (s.includes('pending')) return 0;
    if (s.includes('confirmed')) return 1;
    if (s.includes('processing') || s.includes('preparing')) return 2;
    if (s.includes('delivery')) return 3;
    if (s.includes('delivered')) return 4;
    return 0;
  };

  const filterOrders = () => {
    let filtered = orders;

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(o =>
        (o.orderId || o.OrderId || '').toLowerCase().includes(lowerSearch) ||
        (o.customer?.FullName || o.customer?.fullName || '').toLowerCase().includes(lowerSearch) ||
        (o.driver?.FullName || o.driver?.fullName || '').toLowerCase().includes(lowerSearch) ||
        // ✅ COUPON: Search by coupon code
        (o.CouponCode || '').toLowerCase().includes(lowerSearch)
      );
    }

    if (tabValue > 0) {
      const targetStage = DELIVERY_STAGES[tabValue - 1];
      filtered = filtered.filter(o => {
        const status = o.OrderStatus || o.orderStatus || '';
        return status.toLowerCase().includes(targetStage.toLowerCase());
      });
    }

    return filtered;
  };

  const filteredOrders = filterOrders();

  const toggleExpand = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const renderOrderCard = (order) => {
    const orderId = order.orderId || order.OrderId;
    const status = order.OrderStatus || order.orderStatus || '';
    const isExpanded = expandedOrders[orderId];
    const customer = order.customer;
    const driver = order.driver;
    const products = order.products || [];
    const orderCurrency = order.Currency || order.currency || 'YER';

    // Pending/confirm/cancel logic
    const isPending = status.toLowerCase().includes('pending');
    const paymentMethod = (order.PaymentMethod || order.paymentMethod || '').toLowerCase();
    const isWalletOrder = paymentMethod === 'wallet';
    const transactionStatus = (order.TransactionStatus || order.transactionStatus || '').toLowerCase();
    const isTransactionApproved = transactionStatus === 'approved';
    const canConfirm = isPending && (!isWalletOrder || isTransactionApproved);
    const isConfirming = confirmingOrderId === orderId;
    const isCancelling = cancellingOrderId === orderId;

    // Time calculation
    let timeDisplay = '';
    let displayMinutes = 0;
    if (orderId) {
      const parts = orderId.split('-');
      if (parts.length >= 3) {
        const timestampStr = parts[2].substring(0, 13);
        const timestamp = parseInt(timestampStr);
        if (!isNaN(timestamp)) {
          const orderDate = new Date(timestamp);
          const now = new Date();
          displayMinutes = Math.floor((now.getTime() - orderDate.getTime()) / 60000);
          let hours = orderDate.getHours();
          const minutes = orderDate.getMinutes();
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          timeDisplay = `${hours}:${minutes < 10 ? '0' + minutes : minutes} ${ampm}${isMobile ? '' : ` (${displayMinutes > 0 ? displayMinutes : 0} ${t('min')})`}`;
        }
      }
    }

    const totalAmount = order.TotalAmount || order.totalAmount || 0;
    const deliveryFee = order.DeliveryFee || order.deliveryFee || 0;
    const distance = order.Distance || order.distance || 0;
    const comment = order.Comment || order.comment;
    // ✅ COUPON: Extract coupon data
    const couponCode = order.CouponCode || order.couponCode || null;
    const discountAmount = parseFloat(order.DiscountAmount || order.discountAmount || 0);
    const subTotal = parseFloat(order.SubTotal || order.subTotal || 0);
    const stageIndex = getStageIndex(status);
    const isAssigned = !!(driver);

    return (
      <Grid item xs={12} key={orderId}>
        <Card
          sx={{
            boxShadow: 3,
            border: displayMinutes > 30 ? '3px solid #e74a3b' : 'none',
            borderRadius: { xs: 2, sm: 3 },
            transition: 'all 0.3s ease',
            '&:hover': { 
              transform: isMobile ? 'none' : 'translateY(-2px)', 
              boxShadow: 6 
            }
          }}
        >
          {/* ── HEADER ── */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              bgcolor: isAssigned ? alpha('#4caf50', 0.1) : alpha('#ff9800', 0.1),
              borderBottom: '2px solid #e3e6f0',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1.5, sm: 2 }
            }}
          >
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={{ xs: 1, sm: 2 }} 
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                fontWeight="bold" 
                color="primary"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                #{orderId}
              </Typography>
              
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<AccessTimeIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                  label={timeDisplay || 'N/A'}
                  size="small"
                  color={displayMinutes > 30 ? 'error' : 'default'}
                  sx={{ fontWeight: 'bold', fontSize: { xs: '0.7rem', sm: '0.8rem' }, height: { xs: 24, sm: 28 } }}
                />
                
                {!isMobile && (
                  <Chip
                    label={isAssigned ? t('driver.assigned') || 'Assigned' : t('driver.unassigned') || 'Unassigned'}
                    size="small"
                    color={isAssigned ? 'success' : 'warning'}
                    icon={isAssigned ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <DriverIcon sx={{ fontSize: 16 }} />}
                    sx={{ fontWeight: 'bold', fontSize: '0.8rem', height: 28 }}
                  />
                )}
                
                <Chip
                  label={orderCurrency}
                  size="small"
                  sx={{ fontWeight: 'bold', bgcolor: 'white', fontSize: { xs: '0.7rem', sm: '0.8rem' }, height: { xs: 24, sm: 28 } }}
                />

                {/* Wallet badge */}
                {isWalletOrder && (
                  <Chip
                    icon={<WalletIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                    label={t('common.prepaid') || 'Prepaid'}
                    size="small"
                    sx={{ fontWeight: 'bold', fontSize: { xs: '0.7rem', sm: '0.8rem' }, height: { xs: 24, sm: 28 }, bgcolor: '#D1FAE5', color: '#059669' }}
                  />
                )}

                {/* ✅ COUPON badge */}
                {couponCode && (
                  <Chip
                    label={`🏷️ ${couponCode}`}
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: { xs: '0.7rem', sm: '0.8rem' },
                      height: { xs: 24, sm: 28 },
                      bgcolor: alpha('#059669', 0.1),
                      color: '#059669',
                      fontFamily: 'monospace',
                    }}
                  />
                )}
              </Stack>
            </Stack>

            <Stack 
              direction="row" 
              spacing={1} 
              alignItems="center"
              sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}
            >
              <Chip
                label={getStageTranslation(status)}
                color={getStatusColor(status)}
                size={isMobile ? "small" : "medium"}
                sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.85rem' }, flex: { xs: 1, sm: 'none' } }}
              />
              <IconButton
                onClick={() => toggleExpand(orderId)}
                size={isMobile ? "small" : "medium"}
                sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
              >
                <ExpandMoreIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
            </Stack>
          </Box>

          {/* CONFIRM & CANCEL BUTTONS — Only for Pending orders */}
          {isPending && (
            <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, bgcolor: alpha('#f8f9fc', 0.8), borderBottom: '1px solid #e3e6f0' }}>
              {/* Wallet payment status */}
              {isWalletOrder && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5, mb: 1.5, borderRadius: 2,
                    bgcolor: isTransactionApproved ? alpha('#4caf50', 0.1) : alpha('#ff9800', 0.1),
                    border: `1px solid ${isTransactionApproved ? alpha('#4caf50', 0.3) : alpha('#ff9800', 0.3)}`,
                    display: 'flex', alignItems: 'center', gap: 1
                  }}
                >
                  {isTransactionApproved ? (
                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                  ) : (
                    <AccessTimeIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                  )}
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: isTransactionApproved ? '#2e7d32' : '#e65100', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                  >
                    {isTransactionApproved
                      ? (t('admin.paymentApproved') || 'Payment Approved')
                      : (t('admin.waitingPaymentApproval') || 'Waiting for payment approval')
                    }
                  </Typography>
                </Paper>
              )}

              <Stack direction="row" spacing={1.5}>
                {/* Confirm Button */}
                <Button
                  variant="contained"
                  startIcon={isConfirming ? null : <CheckCircleIcon />}
                  onClick={() => handleConfirmOrder(order)}
                  disabled={!canConfirm || isConfirming}
                  fullWidth
                  sx={{
                    bgcolor: '#4caf50',
                    '&:hover': { bgcolor: '#388e3c' },
                    '&:disabled': { bgcolor: alpha('#4caf50', 0.3), color: '#999' },
                    borderRadius: 2,
                    py: 1.2,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    textTransform: 'none',
                  }}
                >
                  {isConfirming ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    !canConfirm && isWalletOrder
                      ? (t('admin.paymentPending') || 'Payment Pending')
                      : (t('admin.confirmOrder') || 'Confirm Order')
                  )}
                </Button>

                {/* Cancel Button */}
                <Button
                  variant="contained"
                  startIcon={isCancelling ? null : <DeleteIcon />}
                  onClick={() => handleCancelOrder(order)}
                  disabled={isCancelling}
                  fullWidth
                  sx={{
                    bgcolor: '#ef4444',
                    '&:hover': { bgcolor: '#dc2626' },
                    '&:disabled': { bgcolor: alpha('#ef4444', 0.3), color: '#999' },
                    borderRadius: 2,
                    py: 1.2,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    textTransform: 'none',
                  }}
                >
                  {isCancelling ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    t('admin.cancelOrder') || 'Cancel Order'
                  )}
                </Button>
              </Stack>
            </Box>
          )}

          {/* ── CONTENT ── */}
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* Customer & Driver Column */}
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha('#4e73df', 0.05), borderRadius: 2, mb: 2, border: `1px solid ${alpha('#4e73df', 0.1)}` }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                    <Avatar sx={{ bgcolor: '#4e73df', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                      <PersonIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                      {t('driver.customerInfo') || 'Customer'}
                    </Typography>
                  </Stack>
                  <Divider sx={{ mb: 1.5 }} />
                  {customer ? (
                    <Stack spacing={{ xs: 0.8, sm: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          {t('common.name') || 'Name'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                          {customer.FullName || customer.fullName || customer.Username || customer.username || 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          {t('common.phone') || 'Phone'}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <PhoneIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'action.active' }} />
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                            {customer.Phone || customer.phone || 'N/A'}
                          </Typography>
                        </Stack>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          {t('common.address') || 'Address'}
                        </Typography>
                        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                          <LocationIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'action.active', mt: 0.2 }} />
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, wordBreak: 'break-word' }}>
                            {customer.Address || customer.address || customer.City || customer.city || 'N/A'}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                      {t('common.noData') || 'No customer data'}
                    </Typography>
                  )}
                </Paper>

                {/* Driver Info */}
                {driver && (
                  <Paper 
                    elevation={0} 
                    sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha('#4caf50', 0.05), borderRadius: 2, border: `1px solid ${alpha('#4caf50', 0.2)}` }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                      <Avatar sx={{ bgcolor: '#4caf50', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                        <DriverIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                        {t('driver.driverInfo') || 'Driver'}
                      </Typography>
                    </Stack>
                    <Divider sx={{ mb: 1.5 }} />
                    <Stack spacing={{ xs: 0.8, sm: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          {t('common.name') || 'Name'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                          {driver.FullName || driver.fullName || driver.Username || driver.username || 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          {t('common.phone') || 'Phone'}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <PhoneIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'action.active' }} />
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                            {driver.Phone || driver.phone || 'N/A'}
                          </Typography>
                        </Stack>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          {t('driver.vehicleType') || 'Vehicle'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                          {driver.VehicleType || driver.vehicleType || 'N/A'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}
              </Grid>

              {/* Progress & Summary Column */}
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha('#ff9800', 0.05), borderRadius: 2, mb: 2, border: `1px solid ${alpha('#ff9800', 0.1)}` }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                    {t('driver.orderProgress') || 'Order Progress'}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ position: 'relative', pl: { xs: 3, sm: 4 } }}>
                    {DELIVERY_STAGES.map((stage, index) => {
                      const isPast = index < stageIndex;
                      const isCurrent = index === stageIndex;
                      return (
                        <Box key={stage} sx={{ position: 'relative', mb: { xs: 2, sm: 3 } }}>
                          {index < DELIVERY_STAGES.length - 1 && (
                            <Box sx={{ position: 'absolute', left: { xs: -16, sm: -20 }, top: { xs: 16, sm: 20 }, width: 2, height: '100%', bgcolor: isPast || isCurrent ? '#4e73df' : '#e3e6f0' }} />
                          )}
                          <Box sx={{ position: 'absolute', left: { xs: -22, sm: -28 }, top: 0, width: { xs: 12, sm: 16 }, height: { xs: 12, sm: 16 }, borderRadius: '50%', bgcolor: isPast || isCurrent ? '#4e73df' : '#e3e6f0', border: isCurrent ? '3px solid #1cc88a' : 'none', boxShadow: isCurrent ? '0 0 10px rgba(28, 200, 138, 0.5)' : 'none' }} />
                          <Typography variant="body2" fontWeight={isCurrent ? 'bold' : 'normal'} color={isPast || isCurrent ? 'text.primary' : 'text.secondary'} sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                            {getStageTranslation(stage)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>

                {/* ✅ COUPON: Updated Order Summary */}
                <Paper 
                  elevation={0} 
                  sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha('#673ab7', 0.05), borderRadius: 2, border: `1px solid ${alpha('#673ab7', 0.1)}` }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                    {t('driver.orderSummary') || 'Order Summary'}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.5}>
                    {/* SubTotal */}
                    {subTotal > 0 && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                          {t('checkout.subtotal') || 'Subtotal'}
                        </Typography>
                        <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                          {formatPrice(subTotal, orderCurrency)}
                        </Typography>
                      </Stack>
                    )}

                    {/* ✅ COUPON: Discount Line */}
                    {discountAmount > 0 && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, color: '#059669' }}>
                            {t('checkout.couponDiscount') || 'Coupon Discount'}
                          </Typography>
                          {couponCode && (
                            <Chip
                              label={couponCode}
                              size="small"
                              sx={{
                                height: { xs: 18, sm: 20 },
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 'bold',
                                bgcolor: alpha('#059669', 0.1),
                                color: '#059669',
                                fontFamily: 'monospace',
                              }}
                            />
                          )}
                        </Stack>
                        <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, color: '#059669' }}>
                          -{formatPrice(discountAmount, orderCurrency)}
                        </Typography>
                      </Stack>
                    )}

                    {/* Delivery Fee */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                        {t('checkout.deliveryFee') || 'Delivery Fee'}
                      </Typography>
                      <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                        {formatPrice(deliveryFee, orderCurrency)}
                      </Typography>
                    </Stack>

                    {/* Distance */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                        {t('common.distance') || 'Distance'}
                      </Typography>
                      <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                        {parseFloat(distance).toFixed(1)} km
                      </Typography>
                    </Stack>

                    <Divider />

                    {/* Total */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem' } }}>
                        {t('orderStages.common.totalAmount') || 'Total'}
                      </Typography>
                      <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
                        {formatPrice(totalAmount, orderCurrency)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>

              {/* Order Items Column */}
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha('#2196f3', 0.05), borderRadius: 2, border: `1px solid ${alpha('#2196f3', 0.1)}` }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <ShoppingBagIcon sx={{ color: '#2196f3', fontSize: { xs: 20, sm: 24 } }} />
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                      {t('orderStages.common.orderItems') || 'Order Items'}
                    </Typography>
                    <Chip label={products.length} size="small" color="primary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }} />
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  
                  {products.length > 0 ? (
                    <Stack spacing={{ xs: 1.5, sm: 2 }}>
                      {products.map((prod, idx) => {
                        const prodCurrency = prod.Currency || orderCurrency;
                        let subTotalProd = 0;
                        if (prod.SubProducts && Array.isArray(prod.SubProducts)) {
                          prod.SubProducts.forEach((sub) => {
                            subTotalProd += Number(sub.price || sub.Price || 0) * Number(sub.quantity || sub.Quantity || 1);
                          });
                        }
                        const mainPrice = Number(prod.Price || 0);
                        const totalForProduct = (mainPrice + subTotalProd) * Number(prod.Quantity || 1);

                        return (
                          <Box key={idx} sx={{ pb: { xs: 1.5, sm: 2 }, borderBottom: idx < products.length - 1 ? '1px solid #e3e6f0' : 'none' }}>
                            <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="flex-start">
                              <Avatar
                                src={prod.ProductImage}
                                variant="rounded"
                                sx={{ width: { xs: 45, sm: 60 }, height: { xs: 45, sm: 60 }, border: '2px solid', borderColor: 'divider', bgcolor: alpha('#2196f3', 0.1) }}
                                imgProps={{ onError: (e) => { e.target.style.display = 'none'; } }}
                              >
                                <RestaurantIcon sx={{ fontSize: { xs: 20, sm: 28 }, color: '#2196f3' }} />
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, wordBreak: 'break-word', pr: 1 }}>
                                  {prod.Quantity}x {prod.ProductName}
                                </Typography>
                                <Typography variant="body2" color="success.main" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                                  {formatPrice(totalForProduct, prodCurrency)}
                                </Typography>
                                {prod.Size && (
                                  <Chip label={prod.Size} size="small" sx={{ mt: 0.5, height: { xs: 18, sm: 20 }, fontSize: { xs: '0.65rem', sm: '0.7rem' }, bgcolor: alpha('#2196f3', 0.1), color: '#2196f3' }} />
                                )}
                                {prod.SubProducts && prod.SubProducts.length > 0 && (
                                  <Paper elevation={0} sx={{ mt: 1, p: { xs: 0.8, sm: 1 }, bgcolor: 'white', borderRadius: 1, border: '1px solid #e3e6f0' }}>
                                    <Typography variant="caption" fontWeight="bold" color="primary" display="block" gutterBottom sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                                      {t('orderStages.common.customizations') || 'Customizations'}:
                                    </Typography>
                                    <Stack spacing={0.3}>
                                      {prod.SubProducts.map((sub, subIdx) => (
                                        <Typography key={subIdx} variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                          <span>• {sub.name || sub.Name || sub.ProductName || sub.productName || 'N/A'} ({t('cart.quantity') || 'Qty'}: {sub.quantity || sub.Quantity || 1})</span>
                                          <span style={{ fontWeight: 600 }}>{formatPrice(sub.price || sub.Price, prodCurrency)}</span>
                                        </Typography>
                                      ))}
                                    </Stack>
                                  </Paper>
                                )}
                              </Box>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <RestaurantIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                        {t('common.noItems') || 'No items'}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Expandable Section */}
            <Collapse in={isExpanded}>
              {comment && (
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                  <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha('#ffc107', 0.1), borderRadius: 2, borderLeft: `4px solid #ffc107` }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="warning.dark" gutterBottom sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                      {t('orderStages.common.noticeFromUser') || 'Customer Note'}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, wordBreak: 'break-word' }}>
                      {comment}
                    </Typography>
                  </Paper>
                </Box>
              )}
              
              {isMobile && (
                <Box sx={{ mt: 2 }}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: isAssigned ? alpha('#4caf50', 0.1) : alpha('#ff9800', 0.1), borderRadius: 2, border: `2px solid ${isAssigned ? alpha('#4caf50', 0.3) : alpha('#ff9800', 0.3)}` }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {isAssigned ? (
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                      ) : (
                        <DriverIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                      )}
                      <Typography variant="body2" fontWeight="bold" color={isAssigned ? 'success.main' : 'warning.main'} sx={{ fontSize: '0.85rem' }}>
                        {isAssigned ? `${t('driver.assigned') || 'Assigned'} ✓` : t('driver.unassigned') || 'Unassigned'}
                      </Typography>
                    </Stack>
                  </Paper>
                </Box>
              )}
            </Collapse>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, bgcolor: '#f8f9fc', minHeight: '100vh' }}>
      {/* Header */}
      <Stack 
        direction={{ xs: 'column', md: 'row' }} 
        justifyContent="space-between" 
        alignItems={{ xs: 'flex-start', md: 'center' }} 
        spacing={{ xs: 2, md: 0 }}
        mb={{ xs: 3, md: 4 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }}>
            <DeliveryIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#4e73df' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#5a5c69', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                {t('driver.deliveryOrders') || 'Delivery Orders'}
              </Typography>
              {!isMobile && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                  {t('driver.monitorAllDeliveries') || 'Monitor all delivery orders in real-time'}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} sx={{ width: { xs: '100%', md: 'auto' } }}>
          <TextField
            placeholder={t('searchOrder') || 'Search orders...'}
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon color="action" sx={{ mr: 1, fontSize: { xs: 18, sm: 20 } }} /> }}
            sx={{
              bgcolor: 'white', borderRadius: '50px',
              '& .MuiOutlinedInput-root': { borderRadius: '50px', fontSize: { xs: '0.85rem', sm: '0.9rem' } },
              width: { xs: '100%', sm: 280, md: 320 }
            }}
          />
          <IconButton
            color="primary"
            onClick={loadOrders}
            disabled={refreshing}
            sx={{
              bgcolor: '#4e73df', color: 'white',
              width: { xs: '100%', sm: 'auto' }, borderRadius: { xs: 2, sm: '50%' },
              '&:hover': { bgcolor: '#2e59d9' },
              '&:disabled': { bgcolor: alpha('#4e73df', 0.5) }
            }}
          >
            {refreshing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <>
                <RefreshIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                {isMobile && (
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
                    {t('refresh')}
                  </Typography>
                )}
              </>
            )}
          </IconButton>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Box sx={{ mb: { xs: 3, md: 4 }, overflowX: 'auto' }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: { xs: 0.5, sm: 1 } },
            '& .MuiTab-root': {
              textTransform: 'none', fontWeight: 'bold', borderRadius: '50px',
              minHeight: { xs: 36, sm: 40 }, fontSize: { xs: '0.75rem', sm: '0.85rem' },
              px: { xs: 2, sm: 3 }, color: '#858796',
              '&.Mui-selected': { bgcolor: '#4e73df', color: 'white', boxShadow: 2 }
            }
          }}
        >
          <Tab label={t('all') || 'All'} />
          {DELIVERY_STAGES.map((stage) => (
            <Tab key={stage} label={getStageTranslation(stage)} />
          ))}
        </Tabs>
      </Box>

      {/* Orders Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 4, sm: 5 } }}>
          <CircularProgress size={isMobile ? 50 : 60} />
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {filteredOrders.length > 0 ? (
            filteredOrders.map(renderOrderCard)
          ) : (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: { xs: 8, sm: 10 } }}>
                <DeliveryIcon sx={{ fontSize: { xs: 80, sm: 100 }, color: '#858796', mb: 2, opacity: 0.3 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
                  {t('driver.noActiveOrders') || 'No Active Orders'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                  {t('driver.newOrdersWillAppear') || 'New delivery orders will appear here'}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default DeliveryOrders;